import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getImagesByTagSlug } from '../api/apiClient';
import MasonryGrid from '../components/MasonryGrid';
import Seo from '../components/Seo';
import { SITE_URL } from '../seoConfig';
import { formatCompactNumber } from '../utils/format';
import { Loader2, Image as ImageIcon, Eye, FileText } from 'lucide-react';
import NotFoundPage from './NotFoundPage';
import { resolveRedirect } from '../lib/redirects';
import { TAGS_CSS_KEYS, cssVersion, setCssLink, removeCssLink } from '../components/CustomCss';

const BATCH_SIZE = 20;
const TAGS_CSS_LINK_ID = 'explora-tags-css';
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TagPage = () => {
  const { slug, page: pageParam } = useParams();
  const initialPage = Math.max(1, parseInt(pageParam, 10) || 1);

  const [tag, setTag] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [notFound, setNotFound] = useState(false);

  // Load the tag-description CSS as an EXTERNAL stylesheet, only while a tag
  // page is mounted (removed on unmount so it never leaks onto other pages).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/site-blocks`);
        if (cancelled) return;
        const version = cssVersion(data, TAGS_CSS_KEYS);
        setCssLink(TAGS_CSS_LINK_ID, `${API_BASE}/custom-css.css?scope=tags&v=${version}`);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; removeCssLink(TAGS_CSS_LINK_ID); };
  }, []);

  // Direct hit on /page/N shows only that page's slice (photos
  // (N-1)*20 .. N*20). JS "Load more" appends cumulatively client-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;
      try {
        setLoading(true);
        setNotFound(false);
        const data = await getImagesByTagSlug(slug, {
          limit: BATCH_SIZE,
          skip: (initialPage - 1) * BATCH_SIZE,
        });
        if (cancelled) return;
        setTag(data.tag);
        setImages(data.images);
        setTotal(data.total);
        setTotalViews(data.total_views || 0);
        setCurrentPage(initialPage);
      } catch (err) {
        if (cancelled) return;
        if (err.response && err.response.status === 404) {
          // Unknown tag slug — it may be a legacy URL with a configured
          // redirect/410. Check before falling back to NotFound.
          const outcome = await resolveRedirect(window.location.pathname);
          if (cancelled) return;
          if (outcome === 'redirected') return;   // navigating away
          setNotFound(true);                       // 'gone' or 'none' -> 404 page
        } else {
          console.error('Error fetching tag images:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Are we at the last page already?
  const sourceOffset = (currentPage - 1) * BATCH_SIZE + images.length;
  const hasMore = sourceOffset < total;
  const nextPage = currentPage + 1;
  const loadMoreHref = `/photos-hd/${slug}/page/${nextPage}`;

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const data = await getImagesByTagSlug(slug, {
        limit: BATCH_SIZE,
        skip: sourceOffset,
      });
      setImages((prev) => [...prev, ...data.images]);
      setCurrentPage(nextPage);
      window.history.pushState({}, '', loadMoreHref);
    } catch (err) {
      console.error('Error loading more tag images:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (notFound) {
    return <NotFoundPage />;
  }

  const totalPages = Math.max(1, Math.ceil(total / BATCH_SIZE));

  // JSON-LD: page-level CollectionPage + BreadcrumbList only.
  // No photo ItemList / nested ImageObject / numberOfItems — the visible grid is
  // dynamic (sorted by views) and would make per-photo structured data stale.
  const origin = SITE_URL;
  const tagH1 = tag ? tag.cat_h1 : slug;
  const tagUrl = `${origin}/photos-hd/${slug}`;
  const pageUrl = currentPage > 1 ? `${origin}/photos-hd/${slug}/page/${currentPage}` : tagUrl;
  const collectionPageLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: tagH1,
    url: pageUrl,
    description: `${tagH1} photography from Mauritius by local photographers.`,
    isPartOf: { '@type': 'WebSite', name: 'Explora Mauritius', url: origin },
  };
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: origin || '/' },
      { '@type': 'ListItem', position: 2, name: 'Photo Tags', item: `${origin}/photos-hd` },
      { '@type': 'ListItem', position: 3, name: tagH1, item: tagUrl },
    ],
  };

  return (
    <div className="tag-pages min-h-screen bg-white">
      <Seo
        title={`${tagH1} Photos${currentPage > 1 ? ` — Page ${currentPage}` : ''}`}
        description={`Browse ${total} high-resolution ${tagH1} photos from Mauritius by local photographers. Page ${currentPage} of ${totalPages}.`}
        canonical={currentPage > 1 ? `${origin}/photos-hd/${slug}/page/${currentPage}` : tagUrl}
        noindex={currentPage > 1}
        image={images[0]?.image_urls?.full}
        imageAlt={tagH1}
        jsonLd={[collectionPageLd, breadcrumbLd]}
      />
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200 py-8">
        <div className="max-w-full md:max-w-[70%] mx-auto px-4 md:px-4 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {tag ? tag.cat_h1 : slug}
          </h1>
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-600 text-sm mt-4"
            data-testid="tag-stats"
          >
            <span className="inline-flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
              {total} {total === 1 ? 'photo' : 'photos'}
            </span>
            {totalViews > 0 && (
              <span className="inline-flex items-center gap-2">
                <Eye className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                {formatCompactNumber(totalViews)} photo views
              </span>
            )}
            {totalPages > 1 && (
              <span className="inline-flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
          {/* Breadcrumb — placed UNDER the photo/views stats. Same layout as the
              photographer page. .breadcrumb-list keeps it flat despite the
              tag-description CSS (ol list-style / li:first-child margin). */}
          <nav
            className="breadcrumb text-sm text-gray-500 mt-4"
            aria-label="Breadcrumb"
            data-testid="tag-breadcrumb"
          >
            <ol className="breadcrumb-list flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="text-gray-500 no-underline hover:text-gray-900 transition-colors">Explora</Link></li>
              <li aria-hidden="true" className="text-gray-300">&gt;</li>
              <li><Link to="/photos-hd" className="text-gray-500 no-underline hover:text-gray-900 transition-colors">Photo Tags</Link></li>
              <li aria-hidden="true" className="text-gray-300">&gt;</li>
              <li aria-current="page" className="text-gray-700 font-medium truncate max-w-full" title={tagH1}>{tagH1}</li>
            </ol>
          </nav>
          {tag?.description_html && (
            <div
              className="tag-description mt-4"
              dangerouslySetInnerHTML={{ __html: tag.description_html }}
              data-testid="tag-description"
            />
          )}
        </div>
      </div>

      {/* Images Grid */}
      <div className="py-8">
        <MasonryGrid
          images={images}
          hasMore={hasMore}
          onLoadMore={loadMore}
          loadingMore={loadingMore}
          loadMoreHref={loadMoreHref}
          prioritizeFirst={true}
        />
      </div>
    </div>
  );
};

export default TagPage;

